/**
 * VueUseのuseAxiosフックをインポート
 * HTTPリクエストの実行と状態管理を提供する
 */
import { useAxios } from "@vueuse/integrations/useAxios";

/**
 * APIルーター定数をインポート
 * タグカテゴリ関連のAPIエンドポイントURLを提供する
 */
import ApiRouterConstants from "@/clinic/constants/api.router.constants";

/**
 * HTTP通信の共通ユーティリティをインポート
 * Axiosインスタンスと共通設定を提供する
 */
import http from "@/common/utils/http-common-utils";

/**
 * タグカテゴリ関連の型定義をインポート
 * APIリクエスト・レスポンスの型を提供する
 */
import type { ApiBasePaginatorReq, ApiBaseReq } from "@/clinic/typings";

/**
 * タグカテゴリ管理のためのAPIサービス
 * タグカテゴリの取得、作成、更新、削除の操作を提供する
 */
const TagCategoryServices = {
  /**
   * タグカテゴリ一覧を取得する
   * @param {ApiBasePaginatorReq & ApiBaseReq} params - カテゴリ取得のためのリクエストパラメータ
   * @param {() => void} onSuccess - 成功時のコールバック関数
   * @param {() => void} onError - エラー時のコールバック関数
   * @param {() => void} onFinish - 処理完了時のコールバック関数
   * @returns {Promise<any>} APIレスポンス
   */
  fetchCategories: async (
    params: ApiBasePaginatorReq & ApiBaseReq,
    onSuccess: () => void,
    onError: () => void,
    onFinish: () => void
  ) => {
    const { execute } = useAxios(ApiRouterConstants.API_TAG_CATEGORIES, { method: "GET", params: params }, http, {
      immediate: false,
      onSuccess: () => {
        onSuccess();
      },
      onError: () => {
        onError();
      },
      onFinish: () => {
        onFinish();
      },
    });
    return await execute();
  },

  /**
   * タグカテゴリを削除する
   * @param {ApiBaseReq & { id: number }} params - 削除対象のカテゴリ情報（id, clinic_id）
   * @param {() => void} onSuccess - 成功時のコールバック関数
   * @param {(errors: any) => void} onError - エラー時のコールバック関数（エラー情報を受け取る）
   * @returns {Promise<any>} APIレスポンス
   */
  deleteCategory: async (
    params: ApiBaseReq & { id: number },
    onSuccess: () => void,
    onError: (errors: any) => void
  ) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_TAG_CATEGORIES}/${params.id}`,
      { method: "DELETE", params: { clinic_id: params.clinic_id } },
      http,
      {
        immediate: false,
        onSuccess: () => {
          onSuccess();
        },
        onError: (errors: any) => {
          onError(errors.response?.data);
        },
      }
    );
    return await execute();
  },

  /**
   * タグカテゴリを保存する（作成または更新）
   * @param {any} formObject - 保存するカテゴリのフォームデータ
   * @param {() => void} onSuccess - 成功時のコールバック関数
   * @param {(errors: any) => void} onError - エラー時のコールバック関数（エラー情報を受け取る）
   * @returns {Promise<any>} APIレスポンス
   */
  saveCategory: async (formObject: any, onSuccess: () => void, onError: (errors: any) => void) => {
    // 作成か更新かを判定（idの有無で判断）
    const method = formObject.id ? "PATCH" : "POST";
    const url = formObject.id
      ? `${ApiRouterConstants.API_TAG_CATEGORIES}/${formObject.id}`
      : ApiRouterConstants.API_TAG_CATEGORIES;

    // clinic_idをクエリパラメータとして分離
    const { clinic_id, ...data } = formObject;

    const { execute } = useAxios(url, { method: method, params: { clinic_id } }, http, {
      immediate: false,
      onSuccess: () => {
        onSuccess();
      },
      onError: (errors: any) => {
        onError(errors.response?.data);
      },
    });
    return await execute({ data });
  },
};

export default TagCategoryServices;
